import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Edit2,
  Trash2,
  Copy,
  FileSpreadsheet,
  ArrowUp,
  ArrowDown,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAILY_SOURCES = [
  'constant', 'employee_payroll_code', 'employee_name', 'employee_email', 'employee_dni', 'employee_department',
  'date', 'month', 'year', 'worked_hours', 'expected_hours', 'difference_hours', 'extra_hours',
  'night_hours', 'sunday_holiday_hours', 'absence_code_of_day', 'assigned_schedule'
];

const MONTHLY_SOURCES = [
  'constant', 'employee_payroll_code', 'employee_name', 'employee_email', 'employee_dni', 'employee_department',
  'month', 'year', 'concept_code', 'concept_value'
];

const SOURCE_LABELS = {
  es: {
    constant: 'Valor fijo', employee_payroll_code: 'Código de nómina', employee_name: 'Nombre empleado',
    employee_email: 'Email empleado', employee_dni: 'DNI empleado', employee_department: 'Departamento',
    date: 'Fecha', month: 'Mes', year: 'Año', worked_hours: 'Horas trabajadas', expected_hours: 'Horas previstas',
    difference_hours: 'Diferencia', extra_hours: 'Horas extra', night_hours: 'Horas nocturnas',
    sunday_holiday_hours: 'Horas domingo/festivo', absence_code_of_day: 'Motivo del día (vacaciones/baja/no fichó)',
    assigned_schedule: 'Horario asignado', concept_code: 'Código de concepto', concept_value: 'Valor del concepto'
  },
  en: {
    constant: 'Fixed value', employee_payroll_code: 'Payroll code', employee_name: 'Employee name',
    employee_email: 'Employee email', employee_dni: 'Employee DNI', employee_department: 'Department',
    date: 'Date', month: 'Month', year: 'Year', worked_hours: 'Worked hours', expected_hours: 'Expected hours',
    difference_hours: 'Difference', extra_hours: 'Overtime hours', night_hours: 'Night hours',
    sunday_holiday_hours: 'Sunday/holiday hours', absence_code_of_day: 'Reason for the day (vacation/leave/no clock-in)',
    assigned_schedule: 'Assigned schedule', concept_code: 'Concept code', concept_value: 'Concept value'
  }
};

const emptyTemplate = () => ({
  name: '',
  granularity: 'daily_detail',
  file_format: 'csv',
  delimiter: ';',
  encoding: 'utf-8',
  decimal_separator: ',',
  date_format: '%d/%m/%Y',
  include_header: true,
  columns: [],
  concept_mappings: [],
  is_active: true
});

const ExportTemplates = () => {
  const { isAdmin, isGestor } = useAuth();
  const { language } = useLanguage();
  const labels = SOURCE_LABELS[language] || SOURCE_LABELS.es;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/export-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching export templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const availableSources = formData.granularity === 'daily_detail' ? DAILY_SOURCES : MONTHLY_SOURCES;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.columns.length === 0) {
      toast.error(language === 'es' ? 'Añade al menos una columna' : 'Add at least one column');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, columns: formData.columns.map((c, i) => ({ ...c, order: i })) };
      if (editingTemplate) {
        const response = await axios.put(`${API_URL}/export-templates/${editingTemplate.id}`, payload);
        toast.success(language === 'es' ? 'Plantilla actualizada' : 'Template updated');
        setEditingTemplate(response.data);
        setFormData(response.data);
      } else {
        const response = await axios.post(`${API_URL}/export-templates`, payload);
        toast.success(
          response.data.granularity === 'monthly_summary'
            ? (language === 'es' ? 'Plantilla creada. Ahora configura los códigos de cada concepto.' : 'Template created. Now set each concept\'s code.')
            : (language === 'es' ? 'Plantilla creada' : 'Template created')
        );
        // Stay in the dialog, now in "edit" mode, so monthly_summary concept
        // codes (auto-generated by the backend) can be filled in right away.
        setEditingTemplate(response.data);
        setFormData(response.data);
      }
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al guardar' : 'Error saving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData(template);
    setDialogOpen(true);
  };

  const handleDuplicate = (template) => {
    setEditingTemplate(null);
    setFormData({ ...template, name: `${template.name} (${language === 'es' ? 'copia' : 'copy'})` });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta plantilla?' : 'Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/export-templates/${id}`);
      toast.success(language === 'es' ? 'Plantilla eliminada' : 'Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
    setFormData(emptyTemplate());
  };

  const addColumn = () => {
    setFormData(prev => ({
      ...prev,
      columns: [...prev.columns, { order: prev.columns.length, source: availableSources[0], header_label: '', constant_value: '' }]
    }));
  };

  const updateColumn = (idx, patch) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    }));
  };

  const removeColumn = (idx) => {
    setFormData(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== idx) }));
  };

  const moveColumn = (idx, dir) => {
    setFormData(prev => {
      const cols = [...prev.columns];
      const target = idx + dir;
      if (target < 0 || target >= cols.length) return prev;
      [cols[idx], cols[target]] = [cols[target], cols[idx]];
      return { ...prev, columns: cols };
    });
  };

  const updateConcept = (idx, patch) => {
    setFormData(prev => ({
      ...prev,
      concept_mappings: prev.concept_mappings.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    }));
  };

  if (!(isAdmin && isAdmin()) && !(isGestor && isGestor())) {
    return (
      <div className="p-8 text-center text-slate-500">
        {language === 'es' ? 'Solo administradores y gestores pueden gestionar plantillas de exportación.' : 'Only admins and payroll managers can manage export templates.'}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="export-templates-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
            {language === 'es' ? 'Plantillas de Exportación' : 'Export Templates'}
          </h1>
          <p className="text-slate-500">
            {language === 'es'
              ? 'Define cómo exportar los fichajes para tu programa de nómina (A3, Sage, Nominaplus...) — cada gestoría configura su propio formato.'
              : 'Define how to export time records for your payroll software (A3, Sage, Nominaplus...) — each accounting firm configures its own format.'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) closeDialog(); }}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setFormData(emptyTemplate()); setEditingTemplate(null); }}>
              <Plus className="w-4 h-4 mr-2" />
              {language === 'es' ? 'Nueva plantilla' : 'New template'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingTemplate ? (language === 'es' ? 'Editar plantilla' : 'Edit template') : (language === 'es' ? 'Nueva plantilla' : 'New template')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Nombre' : 'Name'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={language === 'es' ? 'Ej: A3 Innuva - Gestoría Pérez' : 'Ex: A3 Innuva - Smith Accounting'}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Tipo de fila' : 'Row type'}</Label>
                  <Select
                    value={formData.granularity}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, granularity: value, columns: [] }))}
                    disabled={!!editingTemplate}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily_detail">{language === 'es' ? 'Detalle diario' : 'Daily detail'}</SelectItem>
                      <SelectItem value="monthly_summary">{language === 'es' ? 'Resumen mensual por concepto' : 'Monthly summary by concept'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Formato de fichero' : 'File format'}</Label>
                  <Select value={formData.file_format} onValueChange={(value) => setFormData(prev => ({ ...prev, file_format: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.file_format === 'csv' && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Delimitador' : 'Delimiter'}</Label>
                    <Select value={formData.delimiter} onValueChange={(value) => setFormData(prev => ({ ...prev, delimiter: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value=";">{language === 'es' ? 'Punto y coma (;)' : 'Semicolon (;)'}</SelectItem>
                        <SelectItem value=",">{language === 'es' ? 'Coma (,)' : 'Comma (,)'}</SelectItem>
                        <SelectItem value="|">{language === 'es' ? 'Barra vertical (|)' : 'Pipe (|)'}</SelectItem>
                        <SelectItem value={'\t'}>Tab</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Codificación' : 'Encoding'}</Label>
                    <Select value={formData.encoding} onValueChange={(value) => setFormData(prev => ({ ...prev, encoding: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="utf-8">UTF-8</SelectItem>
                        <SelectItem value="utf-8-sig">UTF-8 {language === 'es' ? '(con BOM, recomendado para Excel)' : '(with BOM, recommended for Excel)'}</SelectItem>
                        <SelectItem value="latin-1">Latin-1 / Windows-1252</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Separador decimal' : 'Decimal separator'}</Label>
                    <Select value={formData.decimal_separator} onValueChange={(value) => setFormData(prev => ({ ...prev, decimal_separator: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value=",">{language === 'es' ? 'Coma (,)' : 'Comma (,)'}</SelectItem>
                        <SelectItem value=".">{language === 'es' ? 'Punto (.)' : 'Period (.)'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Formato de fecha' : 'Date format'}</Label>
                    <Select value={formData.date_format} onValueChange={(value) => setFormData(prev => ({ ...prev, date_format: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="%d/%m/%Y">DD/MM/AAAA</SelectItem>
                        <SelectItem value="%Y-%m-%d">AAAA-MM-DD</SelectItem>
                        <SelectItem value="%d-%m-%Y">DD-MM-AAAA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <Checkbox
                      id="include_header"
                      checked={formData.include_header}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_header: !!checked }))}
                    />
                    <Label htmlFor="include_header" className="cursor-pointer">
                      {language === 'es' ? 'Incluir fila de cabecera' : 'Include header row'}
                    </Label>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{language === 'es' ? 'Columnas' : 'Columns'}</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addColumn}>
                    <Plus className="w-3 h-3 mr-1" />
                    {language === 'es' ? 'Añadir columna' : 'Add column'}
                  </Button>
                </div>
                {formData.columns.length === 0 && (
                  <p className="text-sm text-slate-400 italic">
                    {language === 'es' ? 'Sin columnas todavía.' : 'No columns yet.'}
                  </p>
                )}
                {formData.columns.map((col, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg">
                    <div className="flex flex-col">
                      <button type="button" onClick={() => moveColumn(idx, -1)} disabled={idx === 0} className="disabled:opacity-20">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveColumn(idx, 1)} disabled={idx === formData.columns.length - 1} className="disabled:opacity-20">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Select value={col.source} onValueChange={(value) => updateColumn(idx, { source: value })}>
                      <SelectTrigger className="w-56 shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableSources.map(src => (
                          <SelectItem key={src} value={src}>{labels[src] || src}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={col.header_label}
                      onChange={(e) => updateColumn(idx, { header_label: e.target.value })}
                      placeholder={language === 'es' ? 'Cabecera' : 'Header'}
                      className="flex-1"
                    />
                    {col.source === 'constant' && (
                      <Input
                        value={col.constant_value || ''}
                        onChange={(e) => updateColumn(idx, { constant_value: e.target.value })}
                        placeholder={language === 'es' ? 'Valor' : 'Value'}
                        className="w-28 shrink-0"
                      />
                    )}
                    <button type="button" onClick={() => removeColumn(idx)} className="text-red-500 shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {formData.granularity === 'monthly_summary' && (
                <div className="space-y-3">
                  <Label>{language === 'es' ? 'Códigos de concepto' : 'Concept codes'}</Label>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    {language === 'es'
                      ? 'Aviso para la gestoría/empresa: "Horas extra" es el balance neto del mes (horas trabajadas − horas previstas, sin bajar de 0), igual que en la pantalla Resumen — no la suma de excesos diarios. Confirmad que este criterio coincide con el convenio colectivo antes de usarlo en nómina.'
                      : 'Notice for the accounting firm/company: "Overtime" is the net monthly balance (worked hours − expected hours, floored at 0), same as the Summary screen — not the sum of each day\'s excess. Confirm this matches your collective agreement before using it for payroll.'}
                  </p>
                  {formData.concept_mappings.length === 0 ? (
                    <p className="text-sm text-slate-400 italic">
                      {language === 'es'
                        ? 'Se generan automáticamente al guardar la plantilla por primera vez.'
                        : 'Generated automatically the first time you save the template.'}
                    </p>
                  ) : (
                    <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                      {formData.concept_mappings.map((c, idx) => (
                        <div key={c.concept_key} className="flex items-center gap-2 p-1.5">
                          <Checkbox
                            checked={c.enabled}
                            onCheckedChange={(checked) => updateConcept(idx, { enabled: !!checked })}
                          />
                          <span className="flex-1 text-sm text-slate-700">{c.concept_label}</span>
                          <Input
                            value={c.code}
                            onChange={(e) => updateConcept(idx, { code: e.target.value })}
                            placeholder={language === 'es' ? 'Código' : 'Code'}
                            className="w-32"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </Button>
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" disabled={saving}>
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.length === 0 ? (
          <Card className="border-slate-200 col-span-full">
            <CardContent className="p-8 text-center text-slate-500">
              {language === 'es' ? 'No hay plantillas todavía.' : 'No templates yet.'}
            </CardContent>
          </Card>
        ) : (
          templates.map((tpl) => (
            <Card key={tpl.id} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                      <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{tpl.name}</h3>
                      <div className="flex gap-1.5 mt-1">
                        <Badge variant="secondary">
                          {tpl.granularity === 'monthly_summary'
                            ? (language === 'es' ? 'Resumen mensual' : 'Monthly summary')
                            : (language === 'es' ? 'Detalle diario' : 'Daily detail')}
                        </Badge>
                        <Badge variant="secondary">{tpl.file_format.toUpperCase()}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  {tpl.columns?.length || 0} {language === 'es' ? 'columnas' : 'columns'}
                </p>
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(tpl)}>
                    <Edit2 className="w-3 h-3 mr-1" />
                    {language === 'es' ? 'Editar' : 'Edit'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDuplicate(tpl)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(tpl.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ExportTemplates;
